/**
 * lh-page-fades.js
 * Continuous scroll-driven opacity fades for all page sections.
 *
 * Mirrors the updateScrollFades() system in the preview HTML (lh-shared.js)
 * but adapted for Shopify's .shopify-section wrapper structure:
 *
 *   Hero section  — fades OUT as user scrolls away (tied to scroll position)
 *   All other sections in <main> — opacity 0 when fully below the viewport,
 *     fade IN as they enter from the bottom, fade OUT as they exit the top.
 *
 * Loaded by sections/custom-header.liquid so it runs on every page.
 * No library dependencies.
 */

(function () {
  'use strict';

  /* ── Tuning constants (mirror lh-shared.js values) ── */
  var HERO_FADE_START     = 0.70;   // hero stays fully opaque until this fraction of hero height
  var HERO_FADE_END       = 1.00;   // hero reaches opacity 0 at this fraction of hero height
  var FADE_IN_ZONE        = 0.50;   // fraction of viewport height used for fade-in zone
  var FADE_OUT_VP         = 0.45;   // max viewport fraction used for fade-out threshold
  var FADE_OUT_SEC        = 0.38;   // max section fraction used for fade-out threshold

  /* ─────────────────────────────────────────────────────────── */
  function init() {

    /* Find the main content area — Shopify uses #MainContent */
    var main = document.querySelector('#MainContent, [role="main"], main');
    if (!main) return;

    /* Product pages: disable scroll restoration so iOS Safari doesn't
       restore a stale scroll offset that would immediately fade content */
    var isProductPage = document.body && document.body.classList.contains('template-product');
    if (isProductPage && 'scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    /* All direct-child .shopify-section wrappers in <main> */
    var allSecs = Array.prototype.slice.call(
      main.querySelectorAll(':scope > .shopify-section, :scope > section')
    );
    if (!allSecs.length) return;

    /* Identify the hero section (contains .lh-hero) */
    var heroWrapper = null;
    var scrollSecs  = [];

    allSecs.forEach(function (sec) {
      /* Skip sections that have a fixed/sticky header element inside */
      if (sec.querySelector('[data-custom-header]')) return;

      if (!heroWrapper && sec.querySelector('.lh-hero')) {
        heroWrapper = sec;
      } else {
        scrollSecs.push(sec);
      }
    });

    /* If there's no lh-hero:
       - Product pages: no hero fade, but keep scroll fades for below-fold sections.
         Force the first section (product detail) to stay visible.
       - Other pages: all sections use scroll-based fades — no hero fade */
    if (!heroWrapper) {
      if (isProductPage) {
        /* Product pages: first section (product form) stays fully visible,
           remaining sections still get scroll-based fade-in */
        if (allSecs.length) {
          allSecs[0].style.opacity = '1';
          scrollSecs = allSecs.slice(1).filter(function (sec) {
            return !sec.querySelector('[data-custom-header]');
          });
        }
      } else {
        scrollSecs = allSecs.filter(function (sec) {
          return !sec.querySelector('[data-custom-header]');
        });
      }
    }

    /* ── Respect prefers-reduced-motion: skip all fades ── */
    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      if (heroWrapper) heroWrapper.style.opacity = '1';
      scrollSecs.forEach(function (sec) { sec.style.opacity = '1'; });
      return;
    }

    /* ── Core fade calculation (runs every rAF tick on scroll) ── */
    function updateFades() {
      var vh      = window.innerHeight;
      var scrollY = window.scrollY;

      /* Hero: opacity tied directly to scroll position */
      if (heroWrapper) {
        var heroH     = heroWrapper.offsetHeight;
        var fadeStart = heroH * HERO_FADE_START + 50;
        var fadeEnd   = heroH * HERO_FADE_END;
        var heroOp;
        if (scrollY <= fadeStart) heroOp = 1;
        else if (scrollY >= fadeEnd) heroOp = 0;
        else heroOp = 1 - ((scrollY - fadeStart) / (fadeEnd - fadeStart));
        heroWrapper.style.opacity = heroOp.toFixed(3);
      }

      /* All other sections in <main> */
      scrollSecs.forEach(function (sec) {
        var rect  = sec.getBoundingClientRect();
        var secH  = rect.height;
        var op;

        if (rect.top >= vh) {
          /* Entirely below the viewport — invisible */
          op = 0;

        } else if (rect.top > 0) {
          /* Entering from the bottom — fade in over bottom 50% of viewport */
          var fadeInPx = vh * FADE_IN_ZONE;
          op = Math.max(0, Math.min(1, (vh - rect.top) / fadeInPx));

        } else {
          /* Section top has scrolled above the viewport top.
             Fade out once only a small strip of the section remains visible. */
          var remaining     = Math.max(0, rect.bottom);
          var fadeOutThresh = Math.min(vh * FADE_OUT_VP, secH * FADE_OUT_SEC);
          op = remaining >= fadeOutThresh
            ? 1
            : Math.max(0, remaining / (fadeOutThresh || 1));
        }

        sec.style.opacity = op.toFixed(3);
      });
    }

    /* ── Scroll listener throttled with rAF ── */
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          updateFades();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    /* ── Recalculate on resize (section heights change) ── */
    var resizeTick = false;
    window.addEventListener('resize', function () {
      if (!resizeTick) {
        requestAnimationFrame(function () {
          updateFades();
          resizeTick = false;
        });
        resizeTick = true;
      }
    }, { passive: true });

    /* ── Run immediately so initial state is correct ── */
    updateFades();

    /* ── Re-run after Shopify section editor reloads (theme customizer) ── */
    document.addEventListener('shopify:section:load', function () {
      /* Re-query since new sections may have been added */
      setTimeout(init, 100);
    });
  }

  /* Boot after DOM is ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
